import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const defaultCtx = {
  totalUnread: 0,
  setTotalUnread: () => {},
  resetUnread: () => {},
  soundEnabled: true,
  toggleSound: () => {},
  testNotification: () => {},
  registerHandler: () => {},
  trackThreadParticipation: () => {},
  hasParticipated: () => false,
};

const ChatNotificationContext = createContext(defaultCtx);

export function ChatNotificationProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem('msg_sound') !== 'false'
  );
  const [totalUnread, setTotalUnread] = useState(0);

  // Refs updated each render — avoids stale closures in memoised callbacks
  const soundEnabledRef = useRef(soundEnabled);
  const userRef = useRef(user);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
  useEffect(() => { userRef.current = user; }, [user]);

  // Messages.js registers its state-update handler here (reassigned each render)
  const messageHandlerRef = useRef(null);

  // Track which threads the current user has replied in: "channelId:parentMsgId"
  const threadParticipationsRef = useRef(new Set());

  // Incoming buzz ("missed-call" ring) — owned here so it rings on any page
  const [incomingBuzz, setIncomingBuzz] = useState(null);
  const ringCtxRef = useRef(null);
  const ringTimerRef = useRef(null);
  const buzzTimeoutRef = useRef(null);

  // ── Audio ──────────────────────────────────────────────────────────────────
  // Notification sound: a WAV served from /public, preloaded once and replayed
  // (cloned so rapid notifications can overlap). Respects the mute toggle.
  const soundRef = useRef(null);
  useEffect(() => {
    const a = new Audio('/notification.wav');
    a.preload = 'auto';
    soundRef.current = a;
  }, []);

  const playPing = useCallback(() => {
    if (!soundEnabledRef.current) return;
    try {
      const base = soundRef.current;
      if (!base) return;
      const a = base.cloneNode();
      a.volume = 1.0;
      a.play().catch(() => { /* blocked until first user gesture */ });
    } catch { /* audio unavailable */ }
  }, []);

  // Reactions use the same notification sound.
  const playReactionPing = playPing;

  // Ask for notification permission on the first user gesture (some browsers require
  // a gesture) — complements the on-connect request in the WS effect below.
  useEffect(() => {
    if (!('Notification' in window) || Notification.permission !== 'default') return;
    const ask = () => { Notification.requestPermission().catch(() => {}); };
    window.addEventListener('pointerdown', ask, { once: true });
    return () => window.removeEventListener('pointerdown', ask);
  }, []);

  // ── Buzz ring ("missed-call") — loops until answered/declined; overrides mute ─
  const stopRing = useCallback(() => {
    if (ringTimerRef.current) { clearInterval(ringTimerRef.current); ringTimerRef.current = null; }
  }, []);

  const startRing = useCallback(() => {
    try {
      if (!ringCtxRef.current) ringCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = ringCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const tone = () => {
        const t0 = ctx.currentTime;
        [0, 0.4].forEach(off => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(520, t0 + off);
          osc.frequency.setValueAtTime(660, t0 + off + 0.12);
          gain.gain.setValueAtTime(0.0001, t0 + off);
          gain.gain.exponentialRampToValueAtTime(0.4, t0 + off + 0.03);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + off + 0.34);
          osc.start(t0 + off);
          osc.stop(t0 + off + 0.36);
        });
      };
      stopRing();
      tone();
      ringTimerRef.current = setInterval(tone, 2400);
    } catch { /* AudioContext unavailable */ }
  }, [stopRing]);

  const ackBuzz = useCallback((b, action) => {
    try {
      const token = localStorage.getItem('auth_token');
      fetch(`${API}/api/buzz/ack`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ from_id: b.from_id, action, scope: b.scope, channel_name: b.channel_name || '' }),
      }).catch(() => {});
    } catch { /* ignore */ }
  }, []);

  const answerBuzz = useCallback(() => {
    setIncomingBuzz(b => {
      if (b) {
        ackBuzz(b, 'answered');
        const open = b.scope === 'channel' ? `channel:${b.channel_id}` : `dm:${b.dm_peer_id}`;
        navigate(`/messages?open=${encodeURIComponent(open)}`);
      }
      return null;
    });
    stopRing();
    if (buzzTimeoutRef.current) { clearTimeout(buzzTimeoutRef.current); buzzTimeoutRef.current = null; }
  }, [ackBuzz, navigate, stopRing]);

  const declineBuzz = useCallback(() => {
    setIncomingBuzz(b => { if (b) ackBuzz(b, 'declined'); return null; });
    stopRing();
    if (buzzTimeoutRef.current) { clearTimeout(buzzTimeoutRef.current); buzzTimeoutRef.current = null; }
  }, [ackBuzz, stopRing]);

  // ── Notification dispatcher ────────────────────────────────────────────────
  // Sound: always. Browser (OS) notification: whenever the user is NOT on the
  // messages page (hidden tab or a different page). Toast: fallback when the OS
  // notification can't be shown (permission not granted) and the tab is visible.
  // (On /messages, Messages.js shows its own in-page toast for other conversations.)
  const fireNotification = useCallback((title, body, onNavigate, ping) => {
    (ping || playPing)();
    // Browser (OS) notification for EVERY message, on any page, whenever permission is granted.
    const canOS = 'Notification' in window && Notification.permission === 'granted';
    if (canOS) {
      try {
        const n = new Notification(title, { body, icon: '/logo192.png' });
        n.onclick = () => { window.focus(); onNavigate?.(); n.close(); };
      } catch { /* OS notification failed */ }
    }
    // In-app toast fallback when we can't show an OS notification (permission not granted).
    if (!canOS && document.visibilityState === 'visible' && window.location.pathname !== '/messages') {
      toast(title, {
        description: body,
        action: { label: 'View', onClick: () => onNavigate?.() },
        duration: 6000,
      });
    }
  }, [playPing]);

  // Manual test (🔔 button in the Messages header): play the sound (ignoring mute),
  // request permission if needed, then show a sample browser notification.
  const testNotification = useCallback(async () => {
    try {
      const a = soundRef.current?.cloneNode();
      if (a) { a.volume = 1.0; a.play().catch(() => {}); }
    } catch { /* audio unavailable */ }
    if (!('Notification' in window)) { toast.error('This browser does not support notifications.'); return; }
    let perm = Notification.permission;
    if (perm === 'default') {
      try { perm = await Notification.requestPermission(); } catch { /* */ }
    }
    if (perm === 'granted') {
      try {
        const n = new Notification('🔔 Test notification', {
          body: 'Browser notifications are working — this is how new messages will alert you.',
          icon: '/logo192.png', requireInteraction: true,
        });
        n.onclick = () => { window.focus(); n.close(); };
        toast.success('Browser notification sent.');
      } catch (e) {
        toast.error('Could not show a browser notification: ' + (e?.message || 'unknown error'));
      }
    } else if (perm === 'denied') {
      toast.error('Notifications are blocked. Enable them for this site in your browser settings, then click Test again.');
    } else {
      toast('Please allow notifications when prompted, then click Test again.');
    }
  }, []);

  // Deep-link to one specific message: opens the channel/DM, and Messages.js then
  // scrolls to + flashes that message (and opens its thread when asked).
  const goToMessage = useCallback((target = {}) => {
    const q = new URLSearchParams();
    if (target.channel_id) {
      q.set('open', `channel:${target.channel_id}${target.msg_id ? `:${target.msg_id}` : ''}`);
    } else if (target.dm_user_id) {
      q.set('open', `dm:${target.dm_user_id}${target.message_id ? `:${target.message_id}` : ''}`);
    }
    if (target.thread) q.set('thread', '1');
    const qs = q.toString();
    navigate(`/messages${qs ? `?${qs}` : ''}`);
  }, [navigate]);

  // One status change updates the channel card AND the creator's DM card, so the
  // owner would get two notifications for a single event. Collapse repeats by key.
  const recentNotifyRef = useRef(new Map());
  const notifyOnce = useCallback((key, fn) => {
    const now = Date.now();
    const seen = recentNotifyRef.current;
    if (key && now - (seen.get(key) || 0) < 5000) return;
    if (key) seen.set(key, now);
    fn();
  }, []);

  // Tag changes are high-frequency, so they notify silently (no chime).
  const noPing = useCallback(() => {}, []);

  // ── Central WS message handler ─────────────────────────────────────────────
  const handleWsData = useCallback((data) => {
    const me = userRef.current;
    // Forward to Messages.js for state updates + in-page toasts
    messageHandlerRef.current?.(data);

    switch (data.type) {
      case 'dm_message': {
        const msg = data.message;
        if (msg.sender_id === me?.user_id) break;
        setTotalUnread(n => n + 1);
        const preview = msg.content || (msg.attachments?.length ? '📎 Attachment' : '');
        // A reply-DM carries link_* — send the click straight to the thread it's about.
        const target = msg.link_channel_id
          ? { channel_id: msg.link_channel_id, msg_id: msg.link_msg_id, thread: msg.link_thread }
          : { dm_user_id: msg.sender_id, message_id: msg.message_id };
        // Reply-DMs share a key with their thread_reply event so the author is
        // notified once, not twice. Ordinary DMs pass a null key and always notify.
        notifyOnce(msg.link_reply_id ? `reply:${msg.link_reply_id}` : null, () =>
          fireNotification(`💬 ${msg.sender_name}`, preview, () => goToMessage(target)));
        break;
      }
      case 'channel_message': {
        const msg = data.message;
        if (msg.sender_id === me?.user_id) break;
        setTotalUnread(n => n + 1);
        const chName = data.channel_name || 'Channel';
        fireNotification(`# ${chName}`, `${msg.sender_name}: ${msg.content || '📎'}`,
          () => goToMessage({ channel_id: msg.channel_id, msg_id: msg.msg_id }));
        break;
      }
      case 'thread_reply': {
        const reply = data.message;
        if (reply.sender_id === me?.user_id) break;
        const isParent = data.parent_sender_id === me?.user_id;
        const participated = threadParticipationsRef.current.has(`${reply.channel_id}:${reply.thread_root_id}`);
        const isMentioned = reply.mentions?.some(m => m.user_id === me?.user_id);
        // A mention must notify even if you're neither the thread's author nor a
        // prior participant - otherwise someone pulled into a thread purely via @mention
        // gets no app-wide notification at all (only Messages.js's in-page toast, which
        // only fires while that page happens to be open).
        if (!isParent && !participated && !isMentioned) break;
        if (isMentioned) setTotalUnread(n => n + 1);
        // The author also gets a real DM (sent from the replier); dedupe so they don't
        // get both that and this for the same reply.
        notifyOnce(`reply:${reply.msg_id}`, () =>
          fireNotification(
            isMentioned ? `🔔 ${reply.sender_name} mentioned you in a thread` : `↩ ${reply.sender_name} replied`,
            reply.content || '📎 Attachment',
            () => goToMessage({ channel_id: reply.channel_id, msg_id: reply.thread_root_id, thread: true })));
        break;
      }
      case 'buzz': {
        if (data.from_id === me?.user_id) break;
        stopRing();
        if (buzzTimeoutRef.current) clearTimeout(buzzTimeoutRef.current);
        setIncomingBuzz(data);
        startRing();
        const where = data.scope === 'channel' ? `# ${data.channel_name}` : 'Direct message';
        if (document.visibilityState !== 'visible' && 'Notification' in window && Notification.permission === 'granted') {
          try {
            const n = new Notification(`📞 ${data.from_name} is buzzing`, { body: where, icon: '/logo192.png', tag: 'chat-buzz', requireInteraction: true });
            n.onclick = () => { window.focus(); n.close(); };
          } catch { /* ignore */ }
        }
        buzzTimeoutRef.current = setTimeout(() => {
          stopRing();
          setIncomingBuzz(null);
          buzzTimeoutRef.current = null;
          toast(`📞 Missed buzz from ${data.from_name}`, { description: where, duration: 8000 });
        }, 30000);
        break;
      }
      case 'buzz_ack': {
        if (data.action === 'answered') toast(`✅ ${data.by_name} answered your buzz`, { duration: 5000 });
        else toast(`🚫 ${data.by_name} declined your buzz`, { duration: 5000 });
        break;
      }
      case 'reaction': {
        const n = data.notify;
        if (!n || n.by_id === me?.user_id) break; // notify on others' reactions, not your own
        const isOwner = n.owner_id && n.owner_id === me?.user_id;
        setTotalUnread(x => x + 1);
        const ref = n.ref ? ` ${n.ref}` : '';
        let title, body;
        if (n.state) {
          const label = n.state.charAt(0).toUpperCase() + n.state.slice(1);
          title = isOwner ? `${n.emoji} Your transaction — ${label}`
            : (data.channel_name ? `# ${data.channel_name}` : `${n.emoji} ${label}`);
          body = `${n.by} marked${ref} ${label} ${n.emoji}`;
        } else {
          title = isOwner ? `${n.emoji} ${n.by} reacted to your transaction`
            : (data.channel_name ? `# ${data.channel_name}` : '💬 Reaction');
          body = `${n.by} reacted ${n.emoji}${ref}`;
        }
        fireNotification(title, body, () => (data.scope === 'channel'
          ? goToMessage({ channel_id: data.channel_id, msg_id: data.msg_id })
          : goToMessage({ dm_user_id: n.by_id, message_id: data.message_id })), playReactionPing);
        break;
      }
      case 'tag': {
        const n = data.notify;
        if (!n || n.by_id === me?.user_id) break;          // never your own action
        if (!n.owner_id || n.owner_id !== me?.user_id) break; // the message AUTHOR only
        setTotalUnread(x => x + 1);
        const ref = n.ref ? ` ${n.ref}` : '';
        const verb = n.added ? 'tagged' : 'removed the tag';
        fireNotification(
          data.channel_name ? `# ${data.channel_name}` : '🏷 Tag',
          `${n.by} ${verb} ${n.tag}${ref ? ` on${ref}` : ' on your message'}`,
          () => goToMessage({ channel_id: data.channel_id, msg_id: data.msg_id }),
          noPing,   // silent: tagging is high-frequency
        );
        break;
      }
      // Transaction status changes ride along on the card edits. The same change
      // touches the channel card and the creator's DM card, hence notifyOnce.
      case 'channel_edit':
      case 'dm_edit': {
        const n = data.notify;
        if (!n || n.kind !== 'status') break;
        if (!n.owner_id || n.owner_id !== me?.user_id) break; // the owner only
        const label = (n.status || '').charAt(0).toUpperCase() + (n.status || '').slice(1);
        const ref = n.ref ? ` ${n.ref}` : '';
        notifyOnce(`status:${n.request_id}:${n.status}`, () => {
          setTotalUnread(x => x + 1);
          fireNotification(
            `Transaction ${label}`,
            `Your transaction${ref} is now ${label}`,
            () => (data.type === 'channel_edit'
              ? goToMessage({ channel_id: n.channel_id, msg_id: n.msg_id })
              : goToMessage({ dm_user_id: n.dm_user_id, message_id: n.message_id })),
          );
        });
        break;
      }
      default: break;
    }
  }, [fireNotification, goToMessage, notifyOnce, noPing, startRing, stopRing, playReactionPing]);

  // ── WebSocket (single global connection) ───────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    // Guard the global: iOS Safari tabs don't define Notification, so an unguarded
    // read here throws and (absent an error boundary) blanks the whole app on mobile.
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    let ws;
    let reconnectTimer;
    let pingInterval;
    let destroyed = false;

    const connect = () => {
      if (destroyed) return;
      const wsBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/, 'ws');
      ws = new WebSocket(`${wsBase}/api/ws?token=${encodeURIComponent(token)}`);
      ws.onmessage = (e) => {
        if (e.data === 'pong') return;
        try { handleWsData(JSON.parse(e.data)); } catch { /* malformed frame */ }
      };
      ws.onclose = () => { if (!destroyed) reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => ws.close();
    };

    connect();
    pingInterval = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) ws.send('ping');
    }, 25000);

    return () => {
      destroyed = true;
      clearTimeout(reconnectTimer);
      clearInterval(pingInterval);
      ws?.close();
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Public API ─────────────────────────────────────────────────────────────
  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem('msg_sound', next ? 'true' : 'false');
      return next;
    });
  }, []);

  // Called by Messages.js in its render body (not inside useEffect) so the
  // registered handler always reflects the latest closure / state.
  const registerHandler = useCallback((fn) => {
    messageHandlerRef.current = fn;
  }, []);

  // Called after sending a thread reply so future replies to that thread notify us.
  const trackThreadParticipation = useCallback((channelId, parentMsgId) => {
    threadParticipationsRef.current.add(`${channelId}:${parentMsgId}`);
  }, []);

  const hasParticipated = useCallback((channelId, parentMsgId) => {
    return threadParticipationsRef.current.has(`${channelId}:${parentMsgId}`);
  }, []);

  const resetUnread = useCallback((count = 0) => setTotalUnread(count), []);

  return (
    <ChatNotificationContext.Provider value={{
      totalUnread, setTotalUnread, resetUnread,
      soundEnabled, toggleSound, testNotification,
      registerHandler, trackThreadParticipation, hasParticipated,
    }}>
      {children}
      {incomingBuzz && (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-6 px-4 pointer-events-none">
          <div className="pointer-events-auto w-full max-w-sm rounded-2xl border bg-card shadow-2xl overflow-hidden">
            <div className="flex items-center gap-3 px-4 pt-4">
              <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center animate-pulse shrink-0">
                <span className="text-xl">📞</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground leading-tight truncate">{incomingBuzz.from_name} is buzzing…</p>
                <p className="text-xs text-muted-foreground truncate">{incomingBuzz.scope === 'channel' ? `# ${incomingBuzz.channel_name}` : 'Direct message'}</p>
              </div>
            </div>
            {incomingBuzz.reason && <p className="px-4 pt-2 text-sm text-muted-foreground italic break-words">"{incomingBuzz.reason}"</p>}
            <div className="flex gap-2 p-4">
              <button onClick={declineBuzz} className="flex-1 h-9 rounded-lg bg-muted text-muted-foreground text-sm font-medium hover:bg-muted/80 transition-colors">Decline</button>
              <button onClick={answerBuzz} className="flex-1 h-9 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">Answer</button>
            </div>
          </div>
        </div>
      )}
    </ChatNotificationContext.Provider>
  );
}

export function useChatNotification() {
  return useContext(ChatNotificationContext);
}
